import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type UserRole = 'user' | 'admin' | 'superadmin';

// Cache role in memory for faster access across navigations
let roleCache: { userId: string; role: UserRole; timestamp: number } | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useUserRole = () => {
  const { user } = useAuth();
  const [role, setRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserRole = async (forceRefresh = false) => {
    if (!user) {
      setRole(null);
      setLoading(false);
      roleCache = null;
      return;
    }

    // Set loading to true at the start
    setLoading(true);

    // Check cache first (unless forcing refresh)
    if (!forceRefresh && roleCache && roleCache.userId === user.id) {
      const cacheAge = Date.now() - roleCache.timestamp;
      if (cacheAge < CACHE_DURATION) {
        console.log('useUserRole: Using cached role:', roleCache.role);
        setRole(roleCache.role);
        setLoading(false);
        return;
      }
    }

    console.log('useUserRole: Fetching role from database for user:', user.email);
    try {
      // Direct query instead of RPC to avoid timing issues
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user role:', error);
        setRole('user'); // Default to user role on error
      } else {
        const fetchedRole = (data?.role || 'user') as UserRole;
        console.log('useUserRole: Fetched role:', fetchedRole);
        setRole(fetchedRole);
        
        // Update cache
        roleCache = {
          userId: user.id,
          role: fetchedRole,
          timestamp: Date.now()
        };
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
      setRole('user');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('useUserRole: useEffect triggered, user:', user?.email);
    fetchUserRole();
  }, [user?.id]); // Only depend on user.id to prevent unnecessary refetches

  const hasRole = (requiredRole: UserRole): boolean => {
    if (!role) return false;
    
    const roleHierarchy = {
      user: 1,
      admin: 2,
      superadmin: 3
    };

    return roleHierarchy[role] >= roleHierarchy[requiredRole];
  };

  const isAdmin = (): boolean => {
    return hasRole('admin');
  };

  const isSuperAdmin = (): boolean => {
    return role === 'superadmin';
  };

  const assignRole = async (userId: string, newRole: UserRole) => {
    try {
      // Use secure server-side function for role assignment
      const { data, error } = await supabase
        .rpc('assign_user_role', {
          target_user_id: userId,
          new_role: newRole
        });

      if (error) throw error;
      
      // Refresh the current user's role if they updated their own role
      if (userId === user?.id) {
        fetchUserRole();
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error assigning role:', error);
      return { success: false, error };
    }
  };

  return {
    role,
    loading,
    hasRole,
    isAdmin,
    isSuperAdmin,
    assignRole
  };
};